/*
 * FIXME: This component is not following the rules described in the new design-system documentation:
 *
 * You can read more here: https://www.notion.so/design-system-rules-07b1593e8b844197bc2cb6b5f0c9451d
 *
 * Rules:
 *
 * - It has dependecy with `mobx`
 * - It is a Class component (Maybe there is a limitation for componentDidCatch`)
 *   More info: https://reactjs.org/docs/hooks-faq.html#do-hooks-cover-all-use-cases-for-classes
 * - It is using components out of the design system
 **/
import * as React from 'react'

import { BlankSlate } from '@factorialco/deprecated-design-system'
import * as Sentry from '@sentry/browser'
import { EnvironmentContext } from 'config/environment'
import { isEqual, last } from 'lodash'

import CrashLogo from 'modules/core/components/CrashLogo'
import errorFingerprint, { ERROR_PATTERNS } from 'modules/core/lib/errorFingerprint'

// If blankSlate is true, we don't render the typycal Crash page
// but a blank page
interface Props {
  blankSlate?: boolean
  customCrashComponent?: React.ReactNode
}
interface State {
  error: Error | null
  failed: boolean
}

export default class ErrorBoundary extends React.Component<React.PropsWithChildren<Props>, State> {
  state: State = {
    error: null,
    failed: false,
  }

  static defaultProps = {
    blankSlate: false,
  }

  // We check if:
  // 1) We only have one children
  // 2) If this children has a route prop
  // If both cases are fullfilled, we check if the error state is present
  // and if the route has changed. If so, we set the error to false to let
  // the change of route trigger the loading of the content.
  componentDidUpdate(prevProps: React.PropsWithChildren<Props>) {
    // Check if route has changed to remove the error state
    if (React.Children.count(this.props.children) > 1 || !this.props.children) return

    const routes = (React.Children.only<any>(this.props.children) as React.ReactElement<any>)?.props
      ?.routes

    const prevRoutes = (React.Children.only<any>(prevProps.children) as React.ReactElement<any>)
      ?.props?.routes

    if (!routes || !prevRoutes) return

    const route = last(routes)
    const prevRoute = last(prevRoutes)

    if (this.state.failed && !isEqual(route, prevRoute)) {
      this.removeFailedState()
    }
  }

  // Where the magic happens.
  //
  // We catch all errors that arrive to this component, sending it to sentry
  // and setting the state when that happens to render the error screen.
  // This prevents the error to raise to higher levels.
  componentDidCatch(error: Error, extra: Record<string, any>) {
    Sentry.withScope((scope) => {
      scope.setFingerprint(errorFingerprint(error, ERROR_PATTERNS.dataResource))
      Sentry.captureException(error, {
        extra,
      })
    })
    this.setState({
      failed: true,
      error,
    })
  }

  removeFailedState = () => {
    this.setState({
      error: null,
      failed: false,
    })
  }

  render() {
    const { children, blankSlate, customCrashComponent } = this.props
    const { failed, error } = this.state

    if (!failed) return children

    if (customCrashComponent) {
      return customCrashComponent
    }

    if (blankSlate) {
      return (
        <EnvironmentContext.Consumer>
          {(context) => (
            <BlankSlate
              illustration="alert"
              description={
                context.showErrorsinErrorBoundary && error ? `Error: ${error.message}` : ''
              }
            />
          )}
        </EnvironmentContext.Consumer>
      )
    }

    return <CrashLogo />
  }
}
